import { createContext, useMemo } from 'react';
import { useHistory } from 'react-router';

import { store } from '../utils';
import { notify } from '../Notifications';
import { checkAuth, performLogin, performLogout } from '../../api';
import type { LoginRequest } from '../../api';

type OverrideProviderProps = {
  children: React.ReactNode;
};

type OverrideProviderContextValue = {
  user?: UserType;
  isLoading?: boolean;
  isAuthenticated?: boolean;

  logout?: () => Promise<void>;
  check?: () => Promise<UserType | null>;
  login?: (payload: LoginRequest) => Promise<string | null>;

  startLogin?: () => void;
};

export const OverrideContext = createContext<OverrideProviderContextValue>({});

export const ServerAuthProvider: React.FC<OverrideProviderProps> = ({
  children,
}) => {
  const history = useHistory();
  const value: OverrideProviderContextValue = useMemo(
    () => ({
      isLoading: false,
      isAuthenticated: true,
      user: undefined,

      check: async (): Promise<UserType | null> => {
        store.getAuth();
        const result = await checkAuth();

        return result
          ? {
              email: result.email,
              name: result.full_name,
              nickname: result.username,
            }
          : null;
      },

      logout: async () => {
        await performLogout();
        store.clearAuth();

        history.push('/login');
      },

      login: async (payload: LoginRequest) => {
        try {
          const { access_token } = await performLogin(payload);
          access_token ? store.setAuth(access_token) : store.clearAuth();

          return access_token || null;
        } catch {
          notify.error('Failed to login!');
          return null;
        }
      },

      startLogin: async () => {
        store.clearAuth();
        history.push('/login');
      },
    }),
    [history]
  );

  return (
    <OverrideContext.Provider value={value}>
      {children}
    </OverrideContext.Provider>
  );
};
